import { ESCAPE } from '@angular/cdk/keycodes';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  DestroyRef,
  effect,
  inject,
  Injector,
  signal,
  untracked,
  ViewEncapsulation,
} from '@angular/core';
import { outputToObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { filter } from 'rxjs/operators';

import { localStorageSignal } from '@core/common-legacy';
import {
  TellentAssistantConfigService,
  TellentAssistantThreadsFacade,
} from '@core/tellent-assistant-logic';
import {
  FIXED_OVERLAY_LEVEL,
  IconButtonComponent,
  IconColorDirective,
  OVERLAY_LEVEL,
} from '@core/user-interface-legacy';

import { TellentAssistantPositionStrategy } from './tellent-assistant-position-strategy';
import { TellentAssistantChatPanelComponent } from '../tellent-assistant-chat-panel/tellent-assistant-chat-panel.component';

@Component({
  selector: 'tellent-assistant',
  imports: [IconButtonComponent, IconColorDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation -- styles target body-attached CDK overlay host + backdrop; selectors are namespaced (tellent-assistant-overlay-panel / tellent-assistant-backdrop)
  encapsulation: ViewEncapsulation.None,
  templateUrl: './tellent-assistant.component.html',
  styleUrl: './tellent-assistant.component.less',
})
export class TellentAssistantComponent {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly threadsFacade = inject(TellentAssistantThreadsFacade);
  private readonly config = inject(TellentAssistantConfigService);

  protected readonly open = signal<boolean>(false);
  protected readonly expanded = localStorageSignal<boolean>('tellent-assistant.expanded', false);

  private overlayRef: OverlayRef | null = null;
  private panelRef: ComponentRef<TellentAssistantChatPanelComponent> | null = null;

  constructor() {
    effect(() => {
      const isOpen = this.open();
      untracked(() => (isOpen ? this.attach() : this.detach()));
    });
    effect(() => {
      const ctx = this.config.currentContext();
      const isOpen = this.open();
      if (isOpen) {
        untracked(() => {
          this.threadsFacade.loadThreads();
          this.config.loadStartersFor(ctx);
        });
      }
    });
    effect(() => {
      const expanded = this.expanded();
      this.applyMode(expanded);
      this.panelRef?.setInput('expanded', expanded);
    });
    this.destroyRef.onDestroy(() => this.detach());
  }

  protected toggle(): void {
    this.open.update(v => !v);
  }

  private attach(): void {
    if (this.overlayRef) {
      return;
    }

    const positionStrategy = new TellentAssistantPositionStrategy();

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      panelClass: 'tellent-assistant-overlay-panel',
      backdropClass: 'tellent-assistant-backdrop',
    });

    this.overlayRef.hostElement.style.zIndex = `${FIXED_OVERLAY_LEVEL}`;

    const portalInjector = Injector.create({
      parent: this.injector,
      providers: [{ provide: OVERLAY_LEVEL, useValue: FIXED_OVERLAY_LEVEL }],
    });
    const portal = new ComponentPortal(TellentAssistantChatPanelComponent, null, portalInjector);
    this.panelRef = this.overlayRef.attach(portal);
    this.applyMode(this.expanded());

    // Defer `.open` one frame so the entrance transition fires from the
    // closed-state CSS (transform/opacity/filter on `.tellent-assistant-overlay-panel`).
    requestAnimationFrame(() => {
      this.overlayRef?.overlayElement.classList.add('open');
    });

    this.panelRef.setInput('expanded', this.expanded());

    outputToObservable(this.panelRef.instance.close)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.open.set(false));

    outputToObservable(this.panelRef.instance.expand)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.expanded.set(true));

    outputToObservable(this.panelRef.instance.collapse)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.expanded.set(false));

    this.overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.open.set(false));

    this.overlayRef
      .keydownEvents()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(e => e.keyCode === ESCAPE),
      )
      .subscribe(() => this.open.set(false));
  }

  private applyMode(expanded: boolean): void {
    if (!this.overlayRef) return;
    const pane = this.overlayRef.overlayElement;
    const backdrop = this.overlayRef.backdropElement;
    pane.classList.toggle('expanded', expanded);
    pane.classList.toggle('compact', !expanded);
    if (backdrop) {
      backdrop.classList.toggle('expanded', expanded);
      backdrop.classList.toggle('compact', !expanded);
    }
  }

  private detach(): void {
    if (!this.overlayRef) {
      return;
    }

    this.overlayRef.dispose();
    this.overlayRef = null;
    this.panelRef = null;
  }
}
